import { SIDE } from 'constants/enums';
import { divide } from 'helpers/ramda';
import { isNan } from 'helpers/utils';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { selectedProductSpotPriceSelector } from 'stores/selectedProduct/selectedProduct.selectors';
import { Product } from 'types/IProducts';

import StoreProxy from '../storeProxy';
import { notional, sanitizeMaxQuantity } from './commonFormulae';

const getSelectedProduct = () => {
  const reduxStore = StoreProxy.getState();
  const selectedProduct = selectedProductSelector(reduxStore);
  return selectedProduct;
};

const marginCalculationForSpreads = (
  size: number,
  leverage: number,
  product: Product
) => {
  try {
    const tickSize = getSelectedProduct()?.tick_size;
    const spotPrice = tickSize
      ? selectedProductSpotPriceSelector(Number(tickSize))
      : undefined;

    if (isNan(spotPrice)) {
      throw new Error('Spot price is NaN');
    }
    const notion = notional(size, Number(spotPrice), product);
    if (isNan(notion)) {
      throw new Error('Notional value is NaN');
    }
    if (!notion) {
      throw new Error('Notional value is zero');
    }
    return divide(notion, leverage);
  } catch (error) {
    console.error('Error in marginCalculationForSpreads:', error);
    return null;
  }
};

const maxQuantityForSpreads = ({
  availableBalance,
  leverage,
  commissionRate,
  product,
  side,
  totalPositionSize,
}: {
  availableBalance: number;
  leverage: number;
  commissionRate: number;
  product: Product;
  side: SIDE;
  totalPositionSize: number;
}) => {
  try {
    const tickSize = getSelectedProduct()?.tick_size;
    const spotPrice = tickSize
      ? selectedProductSpotPriceSelector(Number(tickSize))
      : undefined;
    if (isNan(spotPrice)) {
      throw new Error('Spot price must be a valid number');
    }
    if (availableBalance <= 0) {
      throw new Error('Available balance must be greater than zero');
    }
    if (leverage <= 0) {
      throw new Error('Leverage must be greater than zero');
    }
    if (commissionRate < 0) {
      throw new Error('Commission rate cannot be negative');
    }

    const leverageFactor = 1 / leverage;
    const totalCommission = 2 * commissionRate;
    const totalCost = leverageFactor + totalCommission;
    const maxQuantity = availableBalance / (totalCost * Number(spotPrice));
    return sanitizeMaxQuantity({
      maxQuantity,
      totalPositionSize,
      side,
      product,
    });
  } catch (error) {
    console.error('Error in maxQuantityForSpreads:', error);
    return null;
  }
};

export { marginCalculationForSpreads, maxQuantityForSpreads };
